import {
  Dialog,
  DialogTrigger,
  Modal as AriaModal,
  ModalOverlay,
  Heading,
  type DialogProps,
  type ModalOverlayProps,
} from 'react-aria-components'
import { XCloseIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { Button } from '@/components/base/buttons'

// ============================================================================
// Modal Sizes
// ============================================================================

export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | 'full'

const modalSizes: Record<ModalSize, string> = {
  xs: 'max-w-xs',     // 320px - Small alerts
  sm: 'max-w-sm',     // 384px - Confirmations
  md: 'max-w-md',     // 448px - Standard dialogs
  lg: 'max-w-lg',     // 512px - Forms
  xl: 'max-w-xl',     // 576px - Complex forms
  '2xl': 'max-w-2xl', // 672px - Large content
  '3xl': 'max-w-3xl', // 768px - Extra large
  '4xl': 'max-w-4xl', // 896px - Wide dialogs
  '5xl': 'max-w-5xl', // 1024px - Extra wide
  '6xl': 'max-w-6xl', // 1152px - Very wide
  full: 'max-w-7xl',  // 1280px - Full-featured modals
}

// ============================================================================
// Featured Icon (Untitled UI Pattern)
// ============================================================================

export type FeaturedIconVariant = 'brand' | 'gray' | 'error' | 'warning' | 'success'
export type FeaturedIconSize = 'sm' | 'md' | 'lg' | 'xl'

const featuredIconVariants: Record<FeaturedIconVariant, { bg: string; icon: string }> = {
  brand: {
    bg: 'bg-brand-100 dark:bg-brand-900/40',
    icon: 'text-brand-600 dark:text-brand-400',
  },
  gray: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    icon: 'text-gray-600 dark:text-gray-400',
  },
  error: {
    bg: 'bg-error-100 dark:bg-error-900/40',
    icon: 'text-error-600 dark:text-error-400',
  },
  warning: {
    bg: 'bg-warning-100 dark:bg-warning-900/40',
    icon: 'text-warning-600 dark:text-warning-400',
  },
  success: {
    bg: 'bg-success-100 dark:bg-success-900/40',
    icon: 'text-success-600 dark:text-success-400',
  },
}

const featuredIconSizes: Record<FeaturedIconSize, { container: string; icon: string }> = {
  sm: { container: 'h-10 w-10', icon: 'h-5 w-5' },
  md: { container: 'h-12 w-12', icon: 'h-6 w-6' },
  lg: { container: 'h-14 w-14', icon: 'h-7 w-7' },
  xl: { container: 'h-16 w-16', icon: 'h-8 w-8' },
}

export interface FeaturedIconProps {
  children: React.ReactNode
  variant?: FeaturedIconVariant
  size?: FeaturedIconSize
  className?: string
}

/**
 * FeaturedIcon - Untitled UI pattern for prominent icons in modals/empty states
 * Renders an icon inside a colored circular background
 */
export function FeaturedIcon({
  children,
  variant = 'brand',
  size = 'md',
  className,
}: FeaturedIconProps) {
  const variantStyles = featuredIconVariants[variant]
  const sizeStyles = featuredIconSizes[size]

  return (
    <div
      className={cn(
        'flex items-center justify-center rounded-full',
        variantStyles.bg,
        sizeStyles.container,
        className
      )}
    >
      <div className={cn(variantStyles.icon, sizeStyles.icon, '[&>svg]:h-full [&>svg]:w-full')}>
        {children}
      </div>
    </div>
  )
}

// ============================================================================
// Modal Root
// ============================================================================

export interface ModalProps extends Omit<ModalOverlayProps, 'children'> {
  children: React.ReactNode
  size?: ModalSize
}

export function Modal({ children, size = 'md', className, ...props }: ModalProps) {
  return (
    <ModalOverlay
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4',
        'bg-gray-950/70 backdrop-blur-sm',
        'entering:animate-in entering:fade-in-0',
        'exiting:animate-out exiting:fade-out-0'
      )}
      {...props}
    >
      <AriaModal
        className={cn(
          'w-full max-h-[calc(100vh-2rem)] rounded-xl bg-white shadow-xl outline-none dark:bg-gray-900',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95',
          modalSizes[size],
          className
        )}
      >
        {children}
      </AriaModal>
    </ModalOverlay>
  )
}

// ============================================================================
// Modal Content (Dialog wrapper)
// ============================================================================

export interface ModalContentProps extends DialogProps {
  children: React.ReactNode
}

export function ModalContent({ children, className, ...props }: ModalContentProps) {
  return (
    <Dialog className={cn('outline-none', className)} {...props}>
      {children}
    </Dialog>
  )
}

// ============================================================================
// Modal Header
// ============================================================================

export interface ModalHeaderProps {
  children: React.ReactNode
  onClose?: () => void
  /** Show border below header */
  bordered?: boolean
  /** Centered layout (for icon-based modals) */
  centered?: boolean
  /** Featured icon to show above title */
  icon?: React.ReactNode
  /** Featured icon variant */
  iconVariant?: FeaturedIconVariant
  className?: string
}

export function ModalHeader({
  children,
  onClose,
  bordered = true,
  centered = false,
  icon,
  iconVariant = 'brand',
  className,
}: ModalHeaderProps) {
  // Centered layout with featured icon
  if (centered || icon) {
    return (
      <div
        className={cn(
          'flex flex-col items-center gap-4 px-6 pt-6',
          bordered && 'pb-4 border-b border-gray-200 dark:border-gray-700',
          className
        )}
      >
        {icon && <FeaturedIcon variant={iconVariant}>{icon}</FeaturedIcon>}
        <div className="flex flex-col items-center gap-1 text-center">
          {typeof children === 'string' ? (
            <Heading slot="title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {children}
            </Heading>
          ) : (
            children
          )}
        </div>
        {onClose && (
          <Button
            color="tertiary"
            size="sm"
            onPress={onClose}
            className="absolute right-4 top-4 p-1.5"
            aria-label="Close"
            iconLeading={XCloseIcon}
          />
        )}
      </div>
    )
  }

  // Standard horizontal layout
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 px-6 py-4',
        bordered && 'border-b border-gray-200 dark:border-gray-700',
        className
      )}
    >
      <div className="flex-1 min-w-0">
        {typeof children === 'string' ? (
          <Heading slot="title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {children}
          </Heading>
        ) : (
          children
        )}
      </div>
      {onClose && (
        <Button color="tertiary" size="sm" onPress={onClose} className="p-1.5 -mr-1.5 -mt-0.5" iconLeading={XCloseIcon} />
      )}
    </div>
  )
}

// ============================================================================
// Modal Title & Description (for custom headers)
// ============================================================================

export interface ModalTitleProps {
  children: React.ReactNode
  className?: string
}

export function ModalTitle({ children, className }: ModalTitleProps) {
  return (
    <Heading
      slot="title"
      className={cn('text-lg font-semibold text-gray-900 dark:text-gray-100', className)}
    >
      {children}
    </Heading>
  )
}

export interface ModalDescriptionProps {
  children: React.ReactNode
  className?: string
}

export function ModalDescription({ children, className }: ModalDescriptionProps) {
  return (
    <p className={cn('text-sm text-gray-500 dark:text-gray-400', className)}>
      {children}
    </p>
  )
}

// ============================================================================
// Modal Body
// ============================================================================

export interface ModalBodyProps {
  children: React.ReactNode
  className?: string
}

export function ModalBody({ children, className }: ModalBodyProps) {
  return <div className={cn('px-6 py-4', className)}>{children}</div>
}

// ============================================================================
// Modal Footer
// ============================================================================

export interface ModalFooterProps {
  children: React.ReactNode
  /** Layout direction */
  direction?: 'horizontal' | 'vertical'
  /** Alignment */
  align?: 'start' | 'center' | 'end' | 'stretch'
  /** Show border above footer */
  bordered?: boolean
  className?: string
}

export function ModalFooter({
  children,
  direction = 'horizontal',
  align = 'end',
  bordered = true,
  className,
}: ModalFooterProps) {
  const alignmentClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    stretch: 'justify-stretch [&>*]:flex-1',
  }

  return (
    <div
      className={cn(
        'px-6 py-4',
        bordered && 'border-t border-gray-200 dark:border-gray-700',
        direction === 'horizontal' ? 'flex items-center gap-3' : 'flex flex-col gap-3',
        alignmentClasses[align],
        className
      )}
    >
      {children}
    </div>
  )
}

// ============================================================================
// Exports
// ============================================================================

export { DialogTrigger as ModalTrigger }

export default Modal
